import { theme } from "@eai-toolkit-material-ui/theme";
import {
  Box,
  CircularProgress,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import type { MouseEvent, ReactNode } from "react";

import { TableCell } from "./TableCell";

const LoadingOverlay = styled("div")(() => ({
  position: "absolute",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  backgroundColor: "rgba(255, 255, 255, 0.7)",
  zIndex: 1,
}));

export type RequestState = "IDLE" | "LOADING" | "SUCCESS" | "ERROR";

interface Column<T> {
  header: string;
  accessor: (item: T) => ReactNode;
  width?: string;
  padding?: "none" | "normal" | "checkbox";
}

interface DataTableProps<T> {
  items: T[];
  columns: Column<T>[];
  requestState: RequestState;
  error: string | null;
  emptyStateMessage: ReactNode;
  seeAllLink: string;
  /**
   * Called on left-click, Ctrl/Cmd+click, and middle-click (button 1).
   * The event carries ctrlKey/metaKey/button so the handler can open a new tab on
   * modifier gestures. Always use `useRowNavigation` — do not implement this logic inline.
   */
  onRowClick?: (item: T, event?: MouseEvent<Element>) => void;
}

export const DataTable = <T,>({
  items,
  columns,
  requestState,
  error,
  emptyStateMessage,
  seeAllLink,
  onRowClick,
}: DataTableProps<T>) => {
  return (
    <>
      <div style={{ position: "relative" }}>
        {requestState === "LOADING" && (
          <LoadingOverlay>
            <CircularProgress />
          </LoadingOverlay>
        )}
        <TableContainer
          sx={{
            height: 300,
            position: "relative",
            background: "white",
            marginTop: 1,
          }}
        >
          <Table
            stickyHeader
            sx={{
              minWidth: 400,
              "& .MuiTableHead-root": {
                position: "sticky",
                top: 0,
                zIndex: 1,
              },
              "& .MuiTableCell-stickyHeader": {
                backgroundColor: theme.palette.grey[200],
              },
            }}
            size="small"
          >
            <TableHead>
              <TableRow>
                {columns.map((column) => (
                  <TableCell
                    key={column.header}
                    width={column.width}
                    padding={column.padding}
                  >
                    {column.header}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {requestState === "ERROR" && (
                <TableRow>
                  <TableCell
                    colSpan={columns.length}
                    align="center"
                    sx={{ top: "80px" }}
                  >
                    <Typography variant="body2" color="error">
                      {error || "An error occurred while fetching data"}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              {requestState === "SUCCESS" && items.length === 0 && (
                <TableRow>
                  <TableCell
                    colSpan={columns.length}
                    align="center"
                    sx={{ top: "80px" }}
                  >
                    <Typography variant="body2">{emptyStateMessage}</Typography>
                  </TableCell>
                </TableRow>
              )}

              {items.map((item, index) => (
                <TableRow
                  key={index}
                  sx={{
                    "&:last-child td, &:last-child th": { border: 0 },
                    cursor: onRowClick ? "pointer" : "default",
                    height: "48px",
                  }}
                  onClick={(event) => onRowClick?.(item, event)}
                  onAuxClick={(event) => {
                    // Middle mouse button (button 1) does not fire onClick;
                    // forward it so the row can open in a new tab.
                    if (event.button === 1) {
                      onRowClick?.(item, event);
                    }
                  }}
                  hover={!!onRowClick}
                >
                  {columns.map((column) => (
                    <TableCell
                      key={column.header}
                      width={column.width}
                      padding={column.padding}
                      sx={{ paddingRight: "24px" }}
                    >
                      {column.accessor(item)}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </div>
      {requestState === "SUCCESS" && items.length === 100 && (
        <Typography variant="body1" color="text.secondary">
          Displaying the most recent 100 items. For more{" "}
          <Box component="a" href={seeAllLink} sx={{ color: "primary.main" }}>
            visit {seeAllLink.includes("jobs") ? "Jobs" : "Data Explorer"}
          </Box>
          .
        </Typography>
      )}
      {requestState === "SUCCESS" && items.length < 100 && (
        <Typography variant="body1" color="text.secondary">
          Displaying {items.length} items. For more{" "}
          <Box component="a" href={seeAllLink} sx={{ color: "primary.main" }}>
            visit {seeAllLink.includes("jobs") ? "Jobs" : "Data Explorer"}
          </Box>
          .
        </Typography>
      )}
    </>
  );
};
